import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { AIProvider, useAI } from "@/context/AIContext";
import { Toaster } from "@/components/ui/sonner";
import SupportChat from "@/components/SupportChat";
import AIChatAssistant from "@/components/ai/AIChatAssistant";
import DemoToolsPanel from "@/components/demo/DemoToolsPanel";

// Marketing Pages
import HomePage from "@/pages/HomePage";
import PilotPage from "@/pages/PilotPage";
import ContactPage from "@/pages/ContactPage";
import SecurityPage from "@/pages/SecurityPage";

// App Pages
import Login from "@/pages/Login";
import Portal from "@/pages/Portal";
import AccessLink from "@/pages/AccessLink";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import MicrosoftCallback from "@/pages/MicrosoftCallback";
import Dashboard from "@/pages/Dashboard";
import { lazy, Suspense } from "react";
// Heavy/rare routes lazy-loaded so the main bundle is leaner
const Analytics = lazy(() => import("@/pages/Analytics"));
const BusinessAdmin = lazy(() => import("@/pages/BusinessAdmin"));
const AdviserHub = lazy(() => import("@/pages/AdviserHub"));
const MarketingLeadsAdmin = lazy(() => import("@/pages/admin/MarketingLeadsAdmin"));
const PitchRoomsAdmin = lazy(() => import("@/pages/admin/PitchRoomsAdmin"));
const DemoAccessAdmin = lazy(() => import("@/pages/admin/DemoAccessAdmin"));
const PitchRoom = lazy(() => import("@/pages/PitchRoom"));
const DemoAccess = lazy(() => import("@/pages/DemoAccess"));
const DemoPreview = lazy(() => import("@/pages/DemoPreview"));
const PortalPreviewHandler = lazy(() => import("@/pages/portal/PortalPreviewHandler"));
const ServiceLineDetail = lazy(() => import("@/pages/ServiceLineDetail"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
import Settings from "@/pages/Settings";
import Search from "@/pages/Search";
import PortalLogin from "@/pages/portal/PortalLogin";
import PortalAcceptInvite from "@/pages/portal/PortalAcceptInvite";
import { AdviserPortalDashboard, AdviserPortalClient } from "@/pages/portal/AdviserPortal";
import FamilyPortal from "@/pages/portal/FamilyPortal";
import ClientLayout from "@/pages/client/ClientLayout";
import ClientOverview from "@/pages/client/ClientOverview";
import ClientIndividuals from "@/pages/client/ClientIndividuals";
import ClientPortfolio from "@/pages/client/ClientPortfolio";
import IndividualProfile from "@/pages/client/IndividualProfile";
import ClientAdvisers from "@/pages/client/ClientAdvisers";
import AdviserProfile from "@/pages/client/AdviserProfile";
import ClientEntities from "@/pages/client/ClientEntities";
import EntityProfile from "@/pages/client/EntityProfile";
import ClientMatrix from "@/pages/client/ClientMatrix";
import ClientLinks from "@/pages/client/ClientLinks";
import ClientDocuments from "@/pages/client/ClientDocuments";
import ClientWorkflow from "@/pages/client/ClientWorkflow";
import ClientGovernance from "@/pages/client/ClientGovernance";
import ClientAdvice from "@/pages/client/ClientAdvice";
import ClientActivity from "@/pages/client/ClientActivity";
import ClientAudit from "@/pages/client/ClientAudit";
import ClientHistory from "@/pages/client/ClientHistory";
import ClientContinuityManual from "@/pages/client/ClientContinuityManual";
import ClientHandoverLog from "@/pages/client/ClientHandoverLog";
import ClientReviews from "@/pages/client/ClientReviews";
import ClientPortalAccess from "@/pages/client/ClientPortalAccess";
import ClientEntitiesHub from "@/pages/client/ClientEntitiesHub";
import ClientDocumentsHub from "@/pages/client/ClientDocumentsHub";
import ClientKnowledge from "@/pages/client/ClientKnowledge";
import BulkImport from "@/pages/BulkImport";
import DocumentLibrary from "@/pages/DocumentLibrary";
import InAppSupportPage from "@/pages/InAppSupportPage";
import PublicSupportPage from "@/pages/PublicSupportPage";

/**
 * AppRouter - Handles routing with OAuth callback detection
 * 
 * CRITICAL: Check for session_id in URL fragment SYNCHRONOUSLY during render,
 * NOT in useEffect (which runs after first render - too late!)
 * This prevents race conditions with protected routes.
 * 
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
function AppRouter() {
  const location = useLocation();
  
  // Check for OAuth callback (session_id in URL fragment) - SYNCHRONOUS check
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Marketing Website */}
      <Route path="/" element={<HomePage />} />
      <Route path="/pilot" element={<PilotPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/legal/security" element={<SecurityPage />} />
      <Route path="/support" element={<PublicSupportPage />} />
      <Route path="/help" element={<PublicSupportPage />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/portal" element={<Portal />} />
      <Route path="/access/:accessKey" element={<AccessLink />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ForgotPassword />} />
      <Route path="/auth/microsoft/callback" element={<MicrosoftCallback />} />
      
      {/* App Routes */}
      <Route path="/app" element={<Dashboard />} />
      <Route path="/app/analytics" element={<Suspense fallback={null}><Analytics /></Suspense>} />
      <Route path="/app/settings" element={<Settings />} />
      <Route path="/app/search" element={<Search />} />
      <Route path="/app/import" element={<BulkImport />} />
      <Route path="/app/library" element={<DocumentLibrary />} />
      <Route path="/app/support" element={<InAppSupportPage />} />
      <Route path="/app/admin" element={<Suspense fallback={null}><AdminPanel /></Suspense>} />
      <Route path="/app/business-admin" element={<Suspense fallback={null}><BusinessAdmin /></Suspense>} />
      <Route path="/app/adviser-hub" element={<Suspense fallback={null}><AdviserHub /></Suspense>} />
      <Route path="/app/admin/marketing-leads" element={<Suspense fallback={null}><MarketingLeadsAdmin /></Suspense>} />
      <Route path="/app/admin/pitch-rooms" element={<Suspense fallback={null}><PitchRoomsAdmin /></Suspense>} />
      <Route path="/app/admin/demo-access" element={<Suspense fallback={null}><DemoAccessAdmin /></Suspense>} />
      <Route path="/pitch/:slug" element={<Suspense fallback={null}><PitchRoom /></Suspense>} />
      <Route path="/demo-access/:slug" element={<Suspense fallback={null}><DemoAccess /></Suspense>} />
      <Route path="/demo-preview" element={<Suspense fallback={null}><DemoPreview /></Suspense>} />
      <Route path="/app/service-lines/:slId" element={<Suspense fallback={null}><ServiceLineDetail /></Suspense>} />

      {/* Portal Routes (separate auth) */}
      <Route path="/portal/login" element={<PortalLogin />} />
      <Route path="/portal/accept-invite" element={<PortalAcceptInvite />} />
      <Route path="/portal/preview" element={<Suspense fallback={null}><PortalPreviewHandler /></Suspense>} />
      <Route path="/portal/adviser" element={<AdviserPortalDashboard />} />
      <Route path="/portal/adviser/clients/:clientId" element={<AdviserPortalClient />} />
      <Route path="/portal/family" element={<FamilyPortal />} />

      {/* Client Routes */}
      <Route path="/app/clients/:clientId" element={<ClientLayout />}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<ClientOverview />} />
        <Route path="portfolio" element={<ClientPortfolio />} />
        <Route path="individuals" element={<ClientIndividuals />} />
        <Route path="individuals/:individualId" element={<IndividualProfile />} />
        <Route path="advisers" element={<ClientAdvisers />} />
        <Route path="advisers/:adviserId" element={<AdviserProfile />} />
        <Route path="entities" element={<ClientEntitiesHub />} />
        <Route path="entities/:entityId" element={<EntityProfile />} />
        <Route path="matrix" element={<Navigate to="../entities?view=matrix" replace />} />
        <Route path="links" element={<Navigate to="../documents?view=links" replace />} />
        <Route path="documents" element={<ClientDocumentsHub />} />
        <Route path="workflow" element={<ClientWorkflow />} />
        <Route path="knowledge" element={<ClientKnowledge />} />
        <Route path="governance" element={<Navigate to="../knowledge" replace />} />
        <Route path="advice" element={<Navigate to="../knowledge?view=advice" replace />} />
        <Route path="continuity-manual" element={<Navigate to="../knowledge?view=continuity" replace />} />
        <Route path="handover-log" element={<Navigate to="../history?tab=handover" replace />} />
        <Route path="reviews" element={<ClientReviews />} />
        <Route path="portal-access" element={<ClientPortalAccess />} />
        <Route path="activity" element={<ClientActivity />} />
        <Route path="audit" element={<ClientAudit />} />
        <Route path="history" element={<ClientHistory />} />
      </Route>
      
      {/* Legacy redirects for old routes */}
      <Route path="/client-360-demo" element={<Navigate to="/" replace />} />
      <Route path="/client-360-demo/*" element={<Navigate to="/" replace />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * AuthenticatedAIChat - Only show AI chat when user is logged in AND AI is enabled
 */
function AuthenticatedAIChat() {
  const { token } = useAuth();
  const { aiEnabled } = useAI();
  if (!token || !aiEnabled) return null;
  return <AIChatAssistant />;
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <AIProvider>
          <div className="App min-h-screen transition-colors duration-300" style={{ backgroundColor: 'var(--bg-primary, #F9F7F5)' }}>
            <BrowserRouter>
              <AppRouter />
              <SupportChat />
              <AuthenticatedAIChat />
              <DemoToolsPanel />
            </BrowserRouter>
            <Toaster position="top-right" />
          </div>
          </AIProvider>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
